import { useState, useEffect, useCallback, useRef } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ChevronLeft, Plus, Trash2, GripVertical } from "lucide-react"
import { useWorkflow } from "@/context/workflow-context-simple"
import { ColorPicker } from "@/components/color-picker"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { CustomFieldInputTypeStruct, UserReportDto } from "@/types/workflow"
import { useToast } from "@/hooks/use-toast"
import classNames from "classnames"
import { useTranslation } from "@/hooks/use-translation"
import { getLocalizedField } from '@/utils/language-utils';
import { useLanguage } from '@/context/language-context'
import { isDateField, mapCustomFieldInputTypeToColumnType, resolveFieldInputType } from '@/utils/column-type-helpers'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import './edit-field-modal.css'

interface EditFieldModalProps {
  isOpen: boolean
  onClose: () => void
  fieldConfiguration?: any
  addMode?: boolean
  canEditCustomColumns?: boolean
}

// Mapping function
// mapCustomFieldInputTypeToColumnType now lives in @/utils/column-type-helpers so this modal and the
// View Designer's field panel share ONE mapping (they had drifted). The shared version also corrects
// Yes/No: it occupies a Boolean slot server-side, not a Text one.

// Sortable item component for dropdown options
function SortableDropdownOption({
  id,
  children,
  disabled = false
}: {
  id: string,
  children: React.ReactNode,
  disabled?: boolean
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: isDragging ? '#f3f4f6' : undefined,
        cursor: disabled ? 'not-allowed' : 'grab',
      }}
      {...attributes}
      {...(!disabled ? listeners : {})}
    >
      {children}
    </div>
  )
}

// User-friendly labels for input types
const getInputTypeLabel = (inputType: string, t: (key: import('@/translations').TranslationKey) => string): string => {
  switch (inputType) {
    case CustomFieldInputTypeStruct.Text:
      return t('inputTypeText');
    case CustomFieldInputTypeStruct.YesNo:
      return t('inputTypeYesNo');
    case CustomFieldInputTypeStruct.Dropdown:
      return t('inputTypeDropdown');
    case CustomFieldInputTypeStruct.Date:
      return t('inputTypeDate');
    case CustomFieldInputTypeStruct.Number:
      return t('inputTypeNumber');
    case CustomFieldInputTypeStruct.Email:
      return t('inputTypeEmail');
    case CustomFieldInputTypeStruct.Checklist:
      return t('inputTypeChecklist');
    default:
      return inputType;
  }
};

// All possible input types
// Some of these are not used in the UI at the moment, but will be in the future
const allInputTypes = [
  CustomFieldInputTypeStruct.Text,
  CustomFieldInputTypeStruct.YesNo,
  CustomFieldInputTypeStruct.Dropdown,
  CustomFieldInputTypeStruct.Date,
  CustomFieldInputTypeStruct.Number,
  // CustomFieldInputTypeStruct.Files,
  // CustomFieldInputTypeStruct.Link,
  // CustomFieldInputTypeStruct.LastUpdated,
  // CustomFieldInputTypeStruct.OBLink,
  // CustomFieldInputTypeStruct.FBOWorkerLink,
  CustomFieldInputTypeStruct.Email,
  CustomFieldInputTypeStruct.Checklist,
];

export function EditFieldModal({ isOpen, onClose, fieldConfiguration, addMode, canEditCustomColumns = false }: EditFieldModalProps) {
  const { configuration, refreshConfiguration, currentView } = useWorkflow()
  const { toast } = useToast()
  const workflowApi = useWorkflowApi()
  const { t } = useTranslation()
  const { language } = useLanguage()
  // const customFieldIds = configuration?.customFieldsMetadata?.flatMap((meta) => meta.fieldConfigurationIds) || []
  const isCustom = addMode || fieldConfiguration?.isCustomField === true;
  // Stages
  const allStages = configuration?.stageConfigurations || []

  // DnD sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const [fieldName, setFieldName] = useState(addMode ? "" : (getLocalizedField(fieldConfiguration || {}, 'columnName', language) || ""))
  const [inputType, setInputType] = useState(
    addMode
      ? "Dropdown"
      : resolveFieldInputType(fieldConfiguration)
  )
  const [useColorCode, setUseColorCode] = useState(true)
  const [isMultiSelect, setIsMultiSelect] = useState(addMode ? false : (fieldConfiguration?.allowMultipleSelections || false))
  const [dropdownOptions, setDropdownOptions] = useState(
    addMode
      ? [{ value: "", color: "#FFF3E0", code: null }, { value: "", color: "#E3F2FD", code: null }]
      : (fieldConfiguration?.customValues?.length > 0
        ? fieldConfiguration.customValues.map((v: any) => ({
          value: language === 'fr' ? v.titleFrench : v.titleEnglish,
          color: v.color,
          code: v.code
        }))
        : [{ value: "", color: "#FFF3E0", code: null }, { value: "", color: "#E3F2FD", code: null }])
  )
  const [activeColorOption, setActiveColorOption] = useState<number | null>(null)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [visibleStages, setVisibleStages] = useState(() => {
    const stages: any = {}
    for (const s of allStages) {
      stages[s.stageCode] = false
    }
    if (!addMode && fieldConfiguration) {
      for (const s of allStages) {
        stages[s.stageCode] = s.fieldConfigurations.some((f: any) => f.fieldConfigurationId === fieldConfiguration.fieldConfigurationId)
      }
    }
    return stages
  })
  const [saving, setSaving] = useState(false)
  const [optionToDelete, setOptionToDelete] = useState<number | null>(null);
  const [fieldNameError, setFieldNameError] = useState<string | null>(null);
  const [inputTypeError, setInputTypeError] = useState<string | null>(null);

  // Private column state management
  const [isPrivate, setIsPrivate] = useState(addMode ? false : (fieldConfiguration?.isPrivate || false));
  const [authorizedUsers, setAuthorizedUsers] = useState<UserReportDto[]>(
    addMode ? [] : (fieldConfiguration?.authorizedUserList || [])
  );
  const [note, setNote] = useState(addMode ? "" : (fieldConfiguration?.note || ""));
  const [displayNote, setDisplayNote] = useState(addMode ? false : (fieldConfiguration?.displayNote || false));
  
  // Retain column data state management
  const [isRetainDataEnabled, setIsRetainDataEnabled] = useState(addMode ? true : (fieldConfiguration?.isRetainDataEnabled || false));

  // Client visible column state management
  const [isEnabledForClients, setIsEnabledForClients] = useState(addMode ? false : (fieldConfiguration?.isEnabledForClients || false));
  
  // Field formatting state (for Date fields) - use isDateField helper
  const [fieldFormatting, setFieldFormatting] = useState(() => {
    if (!fieldConfiguration) return "ShortDate";
    // Only initialize with fieldFormatting if this is a date field
    return isDateField(fieldConfiguration) 
      ? (fieldConfiguration.fieldFormatting || "ShortDate")
      : "ShortDate";
  });
  
  // Privacy confirmation modal state
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [pendingPrivacyValue, setPendingPrivacyValue] = useState<boolean | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<UserReportDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Track last searched query to prevent duplicate searches
  const lastSearchedQuery = useRef<string>('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle user search query change with debouncing
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(async () => {
      // Skip if query hasn't changed or already searched
      if (userSearchQuery === lastSearchedQuery.current) {
        return;
      }
      const query = userSearchQuery;
      lastSearchedQuery.current = query;

      if (query.length >= 2) {
        setIsSearching(true);
        try {
          const results = await workflowApi.searchUsers(query);
          if (lastSearchedQuery.current !== query) {
            return;
          }
          setSearchResults(results);
          setShowSearchResults(true);
        } catch (error) {
          console.error('User search failed:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 1000);

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [userSearchQuery]); // Only depend on userSearchQuery

  // Cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Clear search state when modal is closed
      setUserSearchQuery("");
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      lastSearchedQuery.current = '';
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    }
  }, [isOpen]);

  // Determine available input types based on available FieldConfigurationIds for the mapped ColumnType
  const availableColumnTypes = configuration?.customFieldsMetadata?.map(meta => meta.inputDataType) || [];

  // For the UI, filter input types based on available column types
  let filteredInputTypes = allInputTypes.filter(type => {
    // For Dropdown, check both single and multi select
    if (type === CustomFieldInputTypeStruct.Dropdown) {
      const single = mapCustomFieldInputTypeToColumnType(type, false);
      const multi = mapCustomFieldInputTypeToColumnType(type, true);
      return (
        availableColumnTypes.includes(single) ||
        availableColumnTypes.includes(multi)
      );
    }
    const mapped = mapCustomFieldInputTypeToColumnType(type, isMultiSelect);
    return availableColumnTypes.includes(mapped);
  });
  // Edge case: if editing and no available types, allow only the current type
  const editingWithNoTypes = !addMode && filteredInputTypes.length === 0 && fieldConfiguration?.customFieldInputTypeCode;
  if (editingWithNoTypes) {
    filteredInputTypes = [fieldConfiguration.customFieldInputTypeCode];
  }

  // Only show the 'no custom fields available' warning and disable controls when adding a new custom field
  const isNewCustomField = addMode && !fieldConfiguration?.fieldConfigurationId;
  const noCustomFieldsAvailable = isNewCustomField && isCustom && filteredInputTypes.length === 0;

  // Determine if TextList is available for Dropdown multi-select
  const isTextListAvailable = configuration?.customFieldsMetadata?.some(meta => meta.inputDataType === 'TextList');
  const disableMultiSelect = isCustom && inputType === 'Dropdown' && !isTextListAvailable;

  // Helper to get original code for an option (edit mode)
  const getOriginalCode = (index: number): string | null => {
    if (!addMode && fieldConfiguration?.customValues?.[index]) {
      return fieldConfiguration.customValues[index].code;
    }
    return null;
  };

  // Remove option with confirmation
  const handleRemoveOption = (index: number) => {
    setOptionToDelete(index);
  };
  const confirmRemoveOption = () => {
    if (optionToDelete !== null) {
      setDropdownOptions(dropdownOptions.filter((_: any, i: number) => i !== optionToDelete));
      setOptionToDelete(null);
    }
  };
  const cancelRemoveOption = () => setOptionToDelete(null);

  const handleAddOption = () => {
    setDropdownOptions([...dropdownOptions, { value: "", color: "#F5F5F5", code: null }])
  }

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...dropdownOptions]
    newOptions[index].value = value
    setDropdownOptions(newOptions)
  }

  const handleColorChange = (index: number, color: string) => {
    const newOptions = [...dropdownOptions]
    newOptions[index].color = color
    setDropdownOptions(newOptions)
    setActiveColorOption(null)
    setShowColorPicker(false)
  }

  const handleStageChange = (stageCode: string, checked: boolean) => {
    setVisibleStages({ ...visibleStages, [stageCode]: checked })
  }

  // User management functions for private columns
  const handleAddUser = (user: UserReportDto) => {
    setAuthorizedUsers([...authorizedUsers, user]);
    setUserSearchQuery("");
    setSearchResults([]);
    setShowSearchResults(false);
    lastSearchedQuery.current = '';
  };

  const handleRemoveUser = (profileId: string) => {
    setAuthorizedUsers(authorizedUsers.filter(user => user.profileId !== profileId));
  };

  // Handle privacy change - show confirmation modal
  const handlePrivacyChange = (newValue: boolean) => {
    // If no change, do nothing
    if (newValue === isPrivate) {
      return;
    }
    
    // Store the pending value and show confirmation modal
    setPendingPrivacyValue(newValue);
    setIsPrivacyModalOpen(true);
  };

  // Confirm privacy change
  const handleConfirmPrivacyChange = () => {
    if (pendingPrivacyValue === null) return;
    
    setIsPrivate(pendingPrivacyValue);
    
    // If making it public, clear authorized users
    if (!pendingPrivacyValue) {
      setAuthorizedUsers([]);
      setUserSearchQuery("");
      setSearchResults([]);
      setShowSearchResults(false);
      lastSearchedQuery.current = '';
    }
    
    // Close modal and reset pending value
    setIsPrivacyModalOpen(false);
    setPendingPrivacyValue(null);
  };

  // Cancel privacy change
  const handleCancelPrivacyChange = () => {
    setIsPrivacyModalOpen(false);
    setPendingPrivacyValue(null);
  };


  // Auto-disable displayNote when note is empty
  useEffect(() => {
    if (!note.trim() && displayNote) {
      setDisplayNote(false);
    }
  }, [note, displayNote]);

  // DnD handler for dropdown options
  const handleDragEnd = (event: any) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      const oldIndex = dropdownOptions.findIndex((_: { value: string; color: string; code?: string | null }, index: number) => `option-${index}` === active.id)
      const newIndex = dropdownOptions.findIndex((_: { value: string; color: string; code?: string | null }, index: number) => `option-${index}` === over.id)
      const newOptions = arrayMove(dropdownOptions, oldIndex, newIndex)
      setDropdownOptions(newOptions)
    }
  }

  const validate = () => {
    let valid = true;
    if (!fieldName.trim()) {
      setFieldNameError("Field name is required");
      valid = false;
    } else {
      setFieldNameError(null);
    }
    if (isCustom && (!inputType || inputType.trim() === "")) {
      setInputTypeError("Input type is required");
      valid = false;
    } else {
      setInputTypeError(null);
    }
    if (isPrivate && authorizedUsers.length === 0) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "At least one user must be selected for private columns",
      });
      valid = false;
    }
    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true)
    try {
      const selectedStageIds = allStages.filter(s => visibleStages[s.stageCode]).map(s => s.stageId)
      let customValues: import("@/types/workflow").CustomValue[] = [];
      if (isCustom && inputType === "Dropdown") {
        const originalValues: import("@/types/workflow").CustomValue[] = fieldConfiguration?.customValues || [];
        const newValues = dropdownOptions.filter((opt: { value: string; color: string; code?: string | null }) => opt.value.trim() !== "");
        const originalMap = new Map(
          originalValues
            .filter((v) => !v.isDeleted)
            .map((v) => [v.code, v])
        );
        if (addMode) {
          // On create, set both languages
          customValues = newValues.map((opt: { value: string; color: string; code?: string | null }) => ({
            code: null,
            titleEnglish: opt.value,
            titleFrench: opt.value,
            color: opt.color,
            isDeleted: false
          }));
        } else {
          // On update, only update the current language
          customValues = newValues.map((opt: { value: string; color: string; code?: string | null }, idx: number) => {
            let original = null;
            if (opt.code) {
              original = originalMap.get(opt.code);
            }
            if (language === 'fr') {
              return {
                code: original ? original.code : null,
                titleEnglish: original ? original.titleEnglish : opt.value,
                titleFrench: opt.value,
                color: opt.color,
                isDeleted: false
              };
            } else {
              return {
                code: original ? original.code : null,
                titleEnglish: opt.value,
                titleFrench: original ? original.titleFrench : opt.value,
                color: opt.color,
                isDeleted: false
              };
            }
          });
        }
      } else if (isCustom && inputType === CustomFieldInputTypeStruct.YesNo) {
        // Yes/No is RECOLOR-ONLY: the two values are fixed, so keep their original codes + titles
        // and persist only the (possibly changed) colors. Also prevents the wipe that happened when
        // customValues stayed empty for a YesNo field — the update payload below sends customValues
        // for ANY custom field, so an empty array would have deleted the Yes/No values.
        const originalValues: import("@/types/workflow").CustomValue[] = fieldConfiguration?.customValues || [];
        const originalMap = new Map(
          originalValues.filter((v) => !v.isDeleted).map((v) => [v.code, v])
        );
        customValues = dropdownOptions.map((opt: { value: string; color: string; code?: string | null }) => {
          const original = opt.code ? originalMap.get(opt.code) : null;
          return {
            code: original ? original.code : (opt.code ?? null),
            titleEnglish: original ? original.titleEnglish : opt.value,
            titleFrench: original ? original.titleFrench : opt.value,
            color: opt.color,
            isDeleted: false
          };
        });
      }
      if (addMode) {
        // POST /api/Workflow/field/custom
        const payload = {
          columnNameEnglish: fieldName,
          columnNameFrench: fieldName,
          color: null,
          customValues: customValues,
          customFieldInputTypeCode: inputType,
          allowMultipleSelections: isMultiSelect,
          isPrivate: Boolean(isPrivate),
          authorizedUserList: isPrivate ? authorizedUsers : [],
          note: note || null,
          displayNote: Boolean(displayNote && note.trim()),
          isRetainDataEnabled: Boolean(isRetainDataEnabled),
          isEnabledForClients: Boolean(isEnabledForClients),
          fieldFormatting: (inputType === "Date") ? fieldFormatting : null,
          stageIds: selectedStageIds,
          currentViewSettingsId: currentView?.viewSettingsId ?? null
        }

        await workflowApi.createCustomFieldConfiguration(payload)
        // Fetch configuration without viewId to preserve current filters
        // skipFilterUpdate=true ensures filters are not overwritten
        await refreshConfiguration(true, null, true)
        onClose()
        toast({
          variant: "success",
          title: "Success",
          description: "Custom field created successfully",
        })
        return
      }
      // Edit mode (existing field)
      const isCustomField = fieldConfiguration?.isCustomField === true;

      // Create a clean base configuration without potentially conflicting fields
      const baseConfig = { ...fieldConfiguration };
      delete baseConfig.isPrivate;
      delete baseConfig.authorizedUserList;

      const fieldConfigPayload = {
        ...baseConfig,
        columnNameEnglish: language === 'en' ? fieldName : fieldConfiguration.columnNameEnglish,
        columnNameFrench: language === 'fr' ? fieldName : fieldConfiguration.columnNameFrench,
        isCustomField: isCustomField,
        allowMultipleSelections: isCustomField ? isMultiSelect : false,
        customFieldInputTypeCode: isCustomField ? inputType : null,
        customValues: isCustomField ? customValues : [],
        // Explicitly set private fields last to ensure they're not overridden
        isPrivate: Boolean(isPrivate),
        authorizedUserList: isPrivate ? authorizedUsers : [],
        note: note || null,
        displayNote: Boolean(displayNote && note.trim()),
        isRetainDataEnabled: Boolean(isRetainDataEnabled),
        isEnabledForClients: Boolean(isEnabledForClients),
        // Use isDateField helper to determine if fieldFormatting should be included
        fieldFormatting: (isCustomField && inputType === "Date") || 
                         (!isCustomField && fieldConfiguration && isDateField(fieldConfiguration))
                         ? fieldFormatting : null,
      }


      await workflowApi.updateFieldConfiguration(
        fieldConfiguration.fieldConfigurationId,
        fieldConfigPayload,
        selectedStageIds,
        currentView?.viewSettingsId ?? null
      )
      // Fetch configuration without viewId to preserve current filters
      // skipFilterUpdate=true ensures filters are not overwritten
      await refreshConfiguration(true, null, true)
      onClose()
      toast({
        variant: "success",
        title: "Success",
        description: "Field updated successfully",
      })
    } catch (error) {
      console.error('Failed to save field:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save field",
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="sm:max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col"
          aria-describedby="dialog-description"
        >
          <div id="dialog-description" className="sr-only">
            {addMode ? t('addCustomField') : t('editField')} - {t('fieldName')} and {t('inputType')} configuration
          </div>
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>{addMode ? t('addCustomField') : t('editField')}</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <div className="space-y-6 overflow-y-auto flex-1 px-1 custom-scrollbar">
            <div>
              <label htmlFor="field-name" className="block text-sm font-medium mb-1">
                {t('fieldName')}
              </label>
              <Input
                id="field-name"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                onBlur={() => {
                  if (!fieldName.trim()) setFieldNameError("Field name is required");
                  else setFieldNameError(null);
                }}
                placeholder={t('fieldName')}
                disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                className={classNames({
                  "border-red-500 focus-visible:ring-red-500": !!fieldNameError
                })}
              />
              {fieldNameError && (
                <div className="text-xs text-red-600 mt-1">{fieldNameError}</div>
              )}
            </div>

            {noCustomFieldsAvailable && (
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-medium">
                {t('noCustomFieldsAvailable')}
              </div>
            )}

            {/* Separator */}
            {isCustom && (!noCustomFieldsAvailable || !isNewCustomField) && <div className="border-t border-gray-200"></div>}

            {isCustom && (!noCustomFieldsAvailable || !isNewCustomField) && (
              <div>
                <label htmlFor="input-type" className="block text-sm font-medium mb-1">
                  {t('inputType')}
                </label>
                <Select value={inputType} onValueChange={(value) => setInputType(value)}
                  disabled={editingWithNoTypes || !canEditCustomColumns || (!addMode && isCustom)}>
                  <SelectTrigger id="input-type"
                    className={classNames({
                      "border-red-500 focus-visible:ring-red-500": !!inputTypeError
                    })}
                  >
                    <SelectValue placeholder={t('inputType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredInputTypes.map(type => (
                      <SelectItem key={type} value={type}>{getInputTypeLabel(type, t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {inputTypeError && (
                  <div className="text-xs text-red-600 mt-1">{inputTypeError}</div>
                )}
              </div>
            )}
            
            {/* Date Formatting Options - Show for both custom and non-custom Date fields */}
            {(() => {
              // Check if field is a Date type using the shared helper
              const shouldShowDateFormatting = isCustom 
                ? inputType === "Date"
                : (fieldConfiguration && isDateField(fieldConfiguration));
              
              return shouldShowDateFormatting && (!noCustomFieldsAvailable || !isNewCustomField) && (
                <div>
                  <label className="block text-sm font-medium mb-3">{t('dateFormatting')}</label>
                  <div className="flex items-center space-x-8">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="format-short-date"
                        name="date-formatting"
                        value="ShortDate"
                        checked={fieldFormatting === "ShortDate"}
                        onChange={(e) => setFieldFormatting(e.target.value)}
                        disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                        className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-opacity-25"
                        style={{
                          color: 'rgb(15, 33, 75)',
                          accentColor: 'rgb(15, 33, 75)',
                          '--tw-ring-color': 'rgba(15, 33, 75, 0.3)'
                        } as React.CSSProperties}
                      />
                      <label htmlFor="format-short-date" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                        {t('shortDateFormat')}
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="format-date-time"
                        name="date-formatting"
                        value="DateTime"
                        checked={fieldFormatting === "DateTime"}
                        onChange={(e) => setFieldFormatting(e.target.value)}
                        disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                        className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-opacity-25"
                        style={{
                          color: 'rgb(15, 33, 75)',
                          accentColor: 'rgb(15, 33, 75)',
                          '--tw-ring-color': 'rgba(15, 33, 75, 0.3)'
                        } as React.CSSProperties}
                      />
                      <label htmlFor="format-date-time" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                        {t('dateTimeFormat')}
                      </label>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {isCustom && inputType === "Dropdown" && (!noCustomFieldsAvailable || !isNewCustomField) && (
              <>
                <div className="flex flex-row items-center gap-x-6 mb-2">
                  <div className="flex items-center">
                    <Checkbox
                      id="color-code"
                      checked={useColorCode}
                      onCheckedChange={(checked) => setUseColorCode(!!checked)}
                      disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                    />
                    <label htmlFor="color-code" className="ml-2 text-sm font-medium">
                      {t('colorCodeInput')}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <Checkbox
                      id="multi-select"
                      checked={isMultiSelect}
                      onCheckedChange={(checked) => setIsMultiSelect(!!checked)}
                      disabled={noCustomFieldsAvailable || disableMultiSelect || !canEditCustomColumns || !addMode}
                    />
                    <label htmlFor="multi-select" className={`ml-2 text-sm font-medium${(disableMultiSelect || !addMode) ? " text-gray-400" : ""}`}>
                      {t('allowMultipleSelections')}
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('dropdownOptions')}</label>
                  {/* Scrollable container for many options */}
                  <div className="pr-1">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={dropdownOptions.map((_: { value: string; color: string; code?: string | null }, index: number) => `option-${index}`)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2">
                          {dropdownOptions.map((option: { value: string; color: string; code?: string | null }, index: number) => (
                            <SortableDropdownOption key={`option-${index}`} id={`option-${index}`} disabled={noCustomFieldsAvailable || !canEditCustomColumns}>
                              <div className="flex items-center gap-2">
                                <GripVertical size={16} className="text-gray-400 cursor-grab" />
                                <Input
                                  value={option.value}
                                  onChange={(e) => handleOptionChange(index, e.target.value)}
                                  className="flex-1"
                                  placeholder={`Option ${index + 1}`}
                                  disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                                />
                                {useColorCode && (
                                  <div
                                    className="w-8 h-8 rounded cursor-pointer"
                                    style={{ backgroundColor: option.color }}
                                    onClick={() => {
                                      if (!noCustomFieldsAvailable) {
                                        setActiveColorOption(index)
                                        setShowColorPicker(true)
                                      }
                                    }}
                                  />
                                )}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveOption(index)}
                                  disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                                  aria-label={t('delete')}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </SortableDropdownOption>
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full flex items-center justify-center gap-1 mt-2"
                    onClick={handleAddOption}
                    disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                  >
                    <Plus size={16} />
                    {t('option')}
                  </Button>
                </div>
                {optionToDelete !== null && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="confirm-delete-title" aria-describedby="confirm-delete-description">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
                      <h3 id="confirm-delete-title" className="text-lg font-semibold mb-4">{t('delete')}</h3>
                      <p id="confirm-delete-description" className="text-gray-600 mb-6">{t('confirmDeleteOption')}</p>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={cancelRemoveOption}>{t('cancel')}</Button>
                        <Button variant="destructive" onClick={confirmRemoveOption}>{t('delete')}</Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Yes/No values — RECOLOR ONLY. The two values are fixed: the labels are read-only and
                there is no add/remove or multi-select. Only the colour swatch is interactive. */}
            {isCustom && inputType === CustomFieldInputTypeStruct.YesNo && !addMode && dropdownOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-1">{t('dropdownOptions')}</label>
                <div className="space-y-2">
                  {dropdownOptions.map((option: { value: string; color: string; code?: string | null }, index: number) => (
                    <div key={`yesno-option-${index}`} className="flex items-center gap-2">
                      <div className="flex-1 flex items-center px-3 py-2 text-sm text-gray-700 bg-gray-50 border rounded">
                        {option.value}
                      </div>
                      <div
                        className="w-8 h-8 rounded border cursor-pointer"
                        style={{ backgroundColor: option.color || '#f0f0f0' }}
                        onClick={() => {
                          if (noCustomFieldsAvailable || !canEditCustomColumns) return
                          setActiveColorOption(index)
                          setShowColorPicker(true)
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={t('colorCodeInput')}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return
                          if (noCustomFieldsAvailable || !canEditCustomColumns) return
                          setActiveColorOption(index)
                          setShowColorPicker(true)
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Separator */}
            <div className="border-t border-gray-200"></div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('visibleOnStages')}</label>
              <div className="space-y-2">
                {allStages.map((stage) => (
                  <div className="flex items-center" key={stage.stageCode}>
                    <Checkbox
                      id={`stage-${stage.stageCode}`}
                      checked={!!visibleStages[stage.stageCode]}
                      onCheckedChange={(checked) => handleStageChange(stage.stageCode, !!checked)}
                      disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                    />
                    <label htmlFor={`stage-${stage.stageCode}`} className="ml-2 text-sm">
                      {getLocalizedField(stage, 'title', language)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200"></div>

            {/* Retain Column Data Toggle - Only for custom fields */}
            {isCustom && (
              <div>
                <label className="block text-sm font-medium mb-2">{t('retainColumnData')}</label>
                <div className="flex items-start">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isRetainDataEnabled}
                      onChange={(e) => setIsRetainDataEnabled(e.target.checked)}
                      disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <div className="ml-3">
                    <div className="text-xs text-gray-500">{t('retainColumnDataDescription')}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Separator */}
            {isCustom && <div className="border-t border-gray-200"></div>}

            {/* Note Section */}
            <div>
              <label htmlFor="field-note" className="block text-sm font-medium mb-1">
                {t('note')}
              </label>
              <Textarea
                id="field-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('fieldNotePlaceholder')}
                disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                className="min-h-[80px] resize-vertical"
                rows={3}
              />
              <div className="text-xs text-gray-500 mt-1">
                {t('noteSupportsHtml')}
              </div>

              {/* Display Note checkbox */}
              <div className="flex items-center mt-3">
                <Checkbox
                  id="display-note"
                  checked={displayNote}
                  onCheckedChange={(checked) => setDisplayNote(!!checked)}
                  disabled={noCustomFieldsAvailable || !canEditCustomColumns || !note.trim()}
                />
                <label htmlFor="display-note" className={`ml-2 text-sm font-medium${!note.trim() ? " text-gray-400" : ""}`}>
                  {t('displayNoteInGrid')}
                </label>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {t('displayNoteHelpText')}
              </div>
            </div>

            {/* Client Visible Column Section */}
            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium mb-3">{t('clientVisibleColumn')}</label>
              <div className="flex items-center space-x-8">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="client-visible-yes"
                    name="client-visible-column"
                    checked={isEnabledForClients}
                    onChange={() => setIsEnabledForClients(true)}
                    disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                    className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-opacity-25"
                    style={{
                      color: 'rgb(15, 33, 75)',
                      accentColor: 'rgb(15, 33, 75)',
                      '--tw-ring-color': 'rgba(15, 33, 75, 0.3)'
                    } as React.CSSProperties}
                  />
                  <label htmlFor="client-visible-yes" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                    {t('yes')}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="client-visible-no"
                    name="client-visible-column"
                    checked={!isEnabledForClients}
                    onChange={() => setIsEnabledForClients(false)}
                    disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                    className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-opacity-25"
                    style={{
                      color: 'rgb(15, 33, 75)',
                      accentColor: 'rgb(15, 33, 75)',
                      '--tw-ring-color': 'rgba(15, 33, 75, 0.3)'
                    } as React.CSSProperties}
                  />
                  <label htmlFor="client-visible-no" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                    {t('no')} <span className="font-normal" style={{ color: 'rgba(15, 33, 75, 0.7)' }}>{t('default')}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Private Column Section - Only visible to users with ViewPrivateColumns permission */}
            {(() => {
              const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode);
              const canViewPrivateColumns = authorizedActions.includes("ViewPrivateColumns");
              return canViewPrivateColumns ? (
                <div className="border-t border-gray-200 pt-4" style={{ minHeight: '120px'}}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-3">{t('privateColumn')}</label>
                <div className="flex items-center space-x-8">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="private-yes"
                      name="private-column"
                      checked={isPrivate}
                      onChange={() => {
                        handlePrivacyChange(true);
                        setIsPrivacyModalOpen(true);
                      }}
                      disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                      className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-opacity-25"
                      style={{
                        color: 'rgb(15, 33, 75)',
                        accentColor: 'rgb(15, 33, 75)',
                        '--tw-ring-color': 'rgba(15, 33, 75, 0.3)'
                      } as React.CSSProperties}
                    />
                    <label htmlFor="private-yes" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                      {t('yes')}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="private-no"
                      name="private-column"
                      checked={!isPrivate}
                      onChange={() => {
                        handlePrivacyChange(false);
                        setIsPrivacyModalOpen(true);
                      }}
                      disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                      className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-opacity-25"
                      style={{
                        color: 'rgb(15, 33, 75)',
                        accentColor: 'rgb(15, 33, 75)',
                        '--tw-ring-color': 'rgba(15, 33, 75, 0.3)'
                      } as React.CSSProperties}
                    />
                    <label htmlFor="private-no" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                      {t('no')} <span className="font-normal" style={{ color: 'rgba(15, 33, 75, 0.7)' }}>{t('default')}</span>
                    </label>
                  </div>
                </div>
              </div>

              {isPrivate && (
                <div className="space-y-4">
                  <div>
                    {/* User Search Input */}
                    <div className="relative">
                      <Input
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        placeholder={t('searchUsersPlaceholder')}
                        disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                        className="mb-2 border-blue-200 focus:border-blue-400 focus:ring-blue-400 focus:ring-opacity-25"
                      />
                      {isSearching && (
                        <div className="absolute right-2 top-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        </div>
                      )}

                      {/* Search Results Dropdown */}
                      {showSearchResults && searchResults.length > 0 && (
                        <div className="absolute z-10 w-full bg-white border border-blue-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                          {searchResults
                            .filter(result => !authorizedUsers.some(user => user.profileId === result.profileId))
                            .map((user) => (
                              <div
                                key={user.profileId}
                                className="p-3 hover:bg-blue-50 cursor-pointer border-b border-blue-100 last:border-b-0 transition-colors duration-150"
                                onClick={() => handleAddUser(user)}
                              >
                                <div className="font-medium text-gray-900">{user.fullName || user.email}</div>
                                {user.fullName && (
                                  <div className="text-sm text-blue-600">{user.email}</div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}

                      {/* No Results Message */}
                      {showSearchResults &&
                        searchResults.filter(result => !authorizedUsers.some(user => user.profileId === result.profileId)).length === 0 &&
                        userSearchQuery.length >= 2 && !isSearching && (
                          <div className="absolute w-full bg-white border border-blue-200 rounded-lg shadow-xl p-3 text-blue-600 text-center" style={{ zIndex: 50 }}>
                            {t('noUsersFound')}
                          </div>
                        )}
                    </div>

                    {/* Selected Users List */}
                    {authorizedUsers.length > 0 && (
                      <div className="mt-3">
                        <div className="flex flex-wrap gap-2">
                          {authorizedUsers.map((user) => (
                            <div
                              key={user.profileId}
                              className="inline-flex items-center text-sm px-3 py-1.5 rounded-full shadow-sm hover:shadow-md transition-all duration-200"
                              style={{
                                backgroundColor: 'rgba(15, 33, 75, 0.2)',
                                color: 'rgb(15, 33, 75)',
                                border: '1px solid rgba(15, 33, 75, 0.3)'
                              }}
                            >
                              <span className="mr-2 font-medium">
                                {user.fullName || user.email}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveUser(user.profileId)}
                                disabled={noCustomFieldsAvailable || !canEditCustomColumns}
                                className="inline-flex items-center justify-center w-4 h-4 rounded-full transition-all duration-150 font-bold text-sm hover:bg-opacity-30"
                                style={{
                                  color: 'rgb(15, 33, 75)',
                                  backgroundColor: 'rgba(15, 33, 75, 0.1)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(15, 33, 75, 0.25)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(15, 33, 75, 0.1)';
                                }}
                                aria-label="Remove user"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
                </div>
              ) : null;
            })()}
          </div>
          <div className="flex justify-between mt-4">
            <Button variant="ghost" className="flex items-center gap-1" onClick={onClose} disabled={saving || !canEditCustomColumns}>
              <ChevronLeft size={16} />
              {t('backToColumns')}
            </Button>
            <div>
              <Button variant="elliptical-outline" className="mr-2" onClick={onClose} disabled={saving || !canEditCustomColumns}>
                {t('cancel')}
              </Button>
              <Button variant="elliptical" onClick={handleSave} disabled={saving || noCustomFieldsAvailable || !canEditCustomColumns}>
                {t('save')}
              </Button>
            </div>
          </div>
        </DialogContent>
        {showColorPicker && activeColorOption !== null && (
          <ColorPicker
            isOpen={showColorPicker}
            onClose={() => setShowColorPicker(false)}
            initialColor={dropdownOptions[activeColorOption].color}
            onColorSelect={(color) => handleColorChange(activeColorOption, color)}
          />
        )}
      </Dialog>
      {/* Privacy Confirmation Modal */}
      <Dialog open={isPrivacyModalOpen} onOpenChange={setIsPrivacyModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              {pendingPrivacyValue ? t('confirmMakePrivate') : t('confirmMakePublic')}
            </DialogTitle>
            <DialogClose />
          </DialogHeader>

          <DialogDescription className="text-sm text-gray-600 mb-4">
            {pendingPrivacyValue 
              ? t('confirmMakePrivateMessage')
              : t('confirmMakePublicMessage')
            }
          </DialogDescription>

          <div className="flex justify-end gap-2">
            <Button variant="elliptical-outline" onClick={handleCancelPrivacyChange}>
              {t('cancel')}
            </Button>
            <Button 
              variant={"elliptical"} 
              onClick={handleConfirmPrivacyChange}
            >
              {t('confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

